import React, { useEffect, useState } from "react";
import { useImmer } from "use-immer";
import { produce } from "immer";
import { useTranslation } from "react-i18next";
import {
    DatePicker,
    Heading,
    Pagination,
    Popover,
    Table,
    Whisper,
} from "rsuite";
import { TreeNode } from "rsuite/esm/internals/Tree/types";

import Axios from "axios";

import constants from "utils/constants";
import { formatDateWithNameOfMonth, timeSince } from "utils/date-helper";

import { getErrorMessage } from "api/defaults";
import {
    loadActionFilters,
    loadActivityLog,
} from "api/environments/tenants/tenant-activity-log";

import {
    ActionType,
    IActionFilter,
    IActivityLog,
    ITenantFeatureEntry,
} from "store/environments/tenants/tenant-activity-log/tenant-activity-log-types";

import useRefreshPage from "hooks/useRefreshPage";
import useRequestCancellation from "hooks/useRequestCancellation";

import SubmittableCheckTreePicker from "components/Common/SubmittableCheckTreePicker/SubmittableCheckTreePicker";
import ActivityLogDetails from "./ActivityLogDetails/ActivityLogDetails";

import "./ActivityLog.less";

interface ComponentProps {
    envMoniker: string;
    tenantMoniker: string;
    tenantId: string;
    showLoader: (isVisible: boolean) => void;
    showError: (errorMessage: string) => void;
}

const ActivityLog: React.FC<ComponentProps> = ({
    envMoniker,
    tenantMoniker,
    tenantId,
    showLoader,
    showError,
}: ComponentProps) => {
    const { t } = useTranslation();
    const cancelTokenSource = useRequestCancellation();

    const [pageIndex, setPageindex] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [pageTotalCount, setPageTotalCount] = useState<number>(0);

    const [startDate, setStartDate] = useState<string | null>(null);
    const [endDate, setEndDate] = useState<string | null>(null);

    const [actionFilters, setActionFilters] = useImmer<IActionFilter[]>([]);
    const [activityLogs, setActivityLogs] = useImmer<IActivityLog[]>([]);

    const [selectedActivityLog, setSelectedActivityLog] =
        useImmer<IActivityLog | null>(null);
    const [selectedActionFilters, setSelectedActionFilters] = useImmer<
        IActionFilter[]
    >([]);

    const [checkTreeValues, setCheckTreeValues] = useState<string[]>([]);

    const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
    const [isDetailsOpen, setIsDetailsOpen] = useState<boolean>(false);
    const [isDataFetched, setIsDataFetched] = useState<boolean>(false);

    const title = t("tenantConfig.activityLog.header");

    const checkTreeFilters: TreeNode[] = actionFilters.map((af) => ({
        label: af.name,
        value: af.name,
        children: af.subFilters?.map((sbf) => ({
            label: sbf.name,
            value: sbf.name,
        })),
    }));

    const getActionName = (data: IActivityLog): string => {
        switch (data.actionType) {
            case ActionType.SwitchingTenantFeature:
                const name = (JSON.parse(data.entryData) as ITenantFeatureEntry)
                    .TenantFeatureName;

                return data.actionTypeName
                    ? `${data.actionTypeName} (${name})`
                    : t("tenantConfig.activityLog.unknown");
        }

        return data.actionTypeName ?? t("tenantConfig.activityLog.unknown");
    };

    const fetchData = (): void => {
        if (envMoniker && tenantMoniker && tenantId && !isDataFetched) {
            showLoader(true);

            Promise.all([
                loadActivityLog(
                    envMoniker,
                    tenantMoniker,
                    pageIndex,
                    pageSize,
                    startDate,
                    endDate,
                    selectedActionFilters,
                    cancelTokenSource
                )
                    .then(([payload, status]) => {
                        if (payload && status === constants.statusCode.OK) {
                            setActivityLogs(payload.items);
                            setPageTotalCount(payload.totalCount);
                            setPageindex(payload.pageIndex);
                        } else {
                            showError(getErrorMessage(String(payload), status));
                        }
                    })
                    .catch((error: Error) => {
                        const axiosError = error;
                        if (!Axios.isCancel(axiosError)) {
                            showError(error.message);
                        }
                    }),

                loadActionFilters(envMoniker, tenantId, cancelTokenSource)
                    .then(([payload, status]) => {
                        if (payload && status === constants.statusCode.OK) {
                            setActionFilters(payload);
                        } else {
                            showError(getErrorMessage(String(payload), status));
                        }
                    })
                    .catch((error: Error) => {
                        const axiosError = error;
                        if (!Axios.isCancel(axiosError)) {
                            showError(error.message);
                        }
                    }),
            ]).then(() => {
                showError("");
                setIsDataFetched(true);
                showLoader(false);
            });
        }
    };

    const handleChangePage = (page: number): void => {
        setPageindex(page);
    };

    const handleChangeLength = (size: number): void => {
        resetPage();
        setPageSize(size);
    };

    const handleChangeStartDate = (date: Date | null) => {
        resetPage();
        setStartDate(date ? date.toISOString().split("T")[0] : date);
    };

    const handleChangeEndDate = (date: Date | null) => {
        resetPage();
        setEndDate(date ? date.toISOString().split("T")[0] : date);
    };

    const handleChangeActionFilters = (values: string[]) => {
        resetPage();

        // Here are considered trees that have a maximum level of 2

        const selectedFilters = produce(actionFilters, (draft) =>
            draft.filter(
                (af) =>
                    values.some((f) => f === af.name) ||
                    af.subFilters?.some((sbf) =>
                        values.some((f) => f === sbf.name)
                    )
            )
        );

        setSelectedActionFilters(
            produce(selectedFilters, (draft) => {
                draft.map((af) => {
                    if (af.subFilters !== null) {
                        af.subFilters = af.subFilters.filter((sbf) =>
                            values.some((f) => f === sbf.name)
                        );
                    }
                });
            })
        );

        setCheckTreeValues(values);
    };

    const handleActivityLogSelect =
        (activityLog: IActivityLog, name?: string) => () => {
            setSelectedActivityLog({
                ...activityLog,
                actionTypeName: name,
            });
            setIsDetailsOpen(true);
        };

    const resetPage = (): void => {
        setPageindex(1);
    };

    useEffect(() => {
        fetchData();
    }, [envMoniker, tenantMoniker, tenantId, isDataFetched]);

    useRefreshPage(() => {
        setIsDataFetched(false);
    });

    useEffect(() => {
        if (envMoniker && tenantMoniker && tenantId && isDataFetched) {
            setIsRefreshing(true);

            loadActivityLog(
                envMoniker,
                tenantMoniker,
                pageIndex,
                pageSize,
                startDate,
                endDate,
                selectedActionFilters,
                cancelTokenSource
            )
                .then(([payload, status]) => {
                    if (payload && status === constants.statusCode.OK) {
                        showError("");
                        setActivityLogs(payload.items);
                        setPageTotalCount(payload.totalCount);
                        setPageindex(payload.pageIndex);
                    } else {
                        showError(getErrorMessage(String(payload), status));
                    }
                    setIsRefreshing(false);
                })
                .catch((error: Error) => {
                    const axiosError = error;
                    if (!Axios.isCancel(axiosError)) {
                        showError(error.message);
                    }
                });
        }
    }, [pageIndex, pageSize, startDate, endDate, selectedActionFilters]);

    return (
        isDataFetched && (
            <>
                <ActivityLogDetails
                    activityLog={selectedActivityLog}
                    isActivityLogDetailsOpen={isDetailsOpen}
                    setActivityLogDetailsOpen={setIsDetailsOpen}
                />
                <div className="activity-log__container">
                    <Heading level={1}>{title}</Heading>

                    <div className="activity-log__date-filter">
                        <SubmittableCheckTreePicker
                            style={{ width: 300 }}
                            data={checkTreeFilters}
                            value={checkTreeValues}
                            disabled={isRefreshing}
                            cascade={false}
                            onChange={(value) =>
                                handleChangeActionFilters(value as string[])
                            }
                            placeholder={t(
                                "tenantConfig.activityLog.actionTypesFilter"
                            )}
                        />
                        <DatePicker
                            style={{ width: 200 }}
                            editable={false}
                            onChange={(date: Date | null) =>
                                handleChangeStartDate(date)
                            }
                            placeholder={t(
                                "tenantConfig.activityLog.startDateFilter"
                            )}
                            disabled={isRefreshing}
                        />
                        <DatePicker
                            style={{ width: 200 }}
                            editable={false}
                            onChange={(date: Date | null) => {
                                handleChangeEndDate(date);
                            }}
                            placeholder={t(
                                "tenantConfig.activityLog.endDateFilter"
                            )}
                            disabled={isRefreshing}
                        />
                    </div>
                    <div className="activity-log__table__container">
                        <Table
                            className="activity-log__table"
                            loading={isRefreshing}
                            data={activityLogs}
                            fillHeight={true}
                        >
                            <Table.Column flexGrow={3} minWidth={200}>
                                <Table.HeaderCell>
                                    {t(
                                        "tenantConfig.activityLog.table.actionTypeName"
                                    )}
                                </Table.HeaderCell>
                                <Table.Cell dataKey="actionTypeName">
                                    {(rowData: IActivityLog) => (
                                        <Whisper
                                            trigger={"hover"}
                                            placement={"top"}
                                            speaker={
                                                <Popover arrow={false}>
                                                    {getActionName(rowData)}
                                                </Popover>
                                            }
                                        >
                                            <div
                                                className="activity-log__list-name"
                                                onClick={handleActivityLogSelect(
                                                    rowData,
                                                    rowData.actionTypeName
                                                )}
                                            >
                                                {getActionName(rowData)}
                                            </div>
                                        </Whisper>
                                    )}
                                </Table.Cell>
                            </Table.Column>

                            <Table.Column flexGrow={1} minWidth={100}>
                                <Table.HeaderCell>
                                    {t("tenantConfig.activityLog.table.status")}
                                </Table.HeaderCell>
                                <Table.Cell dataKey="failureReason">
                                    {(rowData: IActivityLog) => (
                                        <>
                                            {rowData.failureReason ? (
                                                <Whisper
                                                    enterable
                                                    trigger={"hover"}
                                                    placement={"top"}
                                                    speaker={
                                                        <Popover className="activity-log__tooltip">
                                                            {
                                                                rowData.failureReason
                                                            }
                                                        </Popover>
                                                    }
                                                >
                                                    <div
                                                        className={
                                                            "activity-log__status-block--error"
                                                        }
                                                    >
                                                        {t(
                                                            "tenantConfig.activityLog.table.statusFailed"
                                                        )}
                                                    </div>
                                                </Whisper>
                                            ) : (
                                                <div
                                                    className={
                                                        "activity-log__status-block--success"
                                                    }
                                                >
                                                    {t(
                                                        "tenantConfig.activityLog.table.statusSuccess"
                                                    )}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </Table.Cell>
                            </Table.Column>

                            <Table.Column flexGrow={3} minWidth={200}>
                                <Table.HeaderCell>
                                    {t(
                                        "tenantConfig.activityLog.table.username"
                                    )}
                                </Table.HeaderCell>
                                <Table.Cell dataKey="userName">
                                    {(rowData: IActivityLog) => (
                                        <div>
                                            {rowData.userName
                                                ? rowData.userName
                                                : t("unknown")}
                                        </div>
                                    )}
                                </Table.Cell>
                            </Table.Column>

                            <Table.Column flexGrow={1} minWidth={120}>
                                <Table.HeaderCell>
                                    {t(
                                        "tenantConfig.activityLog.table.timeAgo"
                                    )}
                                </Table.HeaderCell>
                                <Table.Cell dataKey="timeStamp">
                                    {(rowData: IActivityLog) => (
                                        <div>
                                            {timeSince(
                                                new Date(rowData.timeStamp)
                                            )}
                                        </div>
                                    )}
                                </Table.Cell>
                            </Table.Column>

                            <Table.Column flexGrow={2} minWidth={200}>
                                <Table.HeaderCell>
                                    {t(
                                        "tenantConfig.activityLog.table.timestamp"
                                    )}
                                </Table.HeaderCell>
                                <Table.Cell dataKey="timeStamp">
                                    {(rowData: IActivityLog) => (
                                        <div>
                                            {formatDateWithNameOfMonth(
                                                new Date(rowData.timeStamp)
                                            )}
                                        </div>
                                    )}
                                </Table.Cell>
                            </Table.Column>
                        </Table>
                        <Pagination
                            layout={["limit", "|", "total", "-", "pager"]}
                            limitOptions={[10, 20]}
                            maxButtons={5}
                            activePage={pageIndex}
                            limit={pageSize}
                            total={pageTotalCount}
                            onChangePage={handleChangePage}
                            onChangeLimit={handleChangeLength}
                            disabled={isRefreshing || activityLogs.length === 0}
                            ellipsis={true}
                            first
                            last
                            prev
                            next
                            boundaryLinks
                        />
                    </div>
                </div>
            </>
        )
    );
};

export default ActivityLog;
